/**
 * Beweging in de binnenkomer van een vacature.
 *
 * Bij het laden bouwt de hero zich in één doorlopende beweging op: het beeld zakt uit een
 * lichte overmaat terug op zijn plek, de hap linksonder tekent zichzelf, en daaroverheen
 * komen de tag, de functietitel, de feiten, de knoppen en de kaarten rechts na elkaar
 * binnen. De titel komt regel voor regel van onder een masker vandaan -- dezelfde beweging
 * als de kop op de homepage, zodat de twee pagina's zich hetzelfde gedragen.
 *
 * Tijdens het scrollen loopt de tekst iets vooruit op de pagina en blijft het beeld juist
 * achter. Alleen dat verschil zie je. De kaarten rechts doen daar niet aan mee: die staan
 * op een telefoon onderaan de binnenkomer als een witte band en horen daar aan de rand
 * vast te blijven zitten -- vandaar dat de parallax alleen vanaf lg loopt.
 *
 * set() en to() met expliciete waarden, geen from(): een from()-tween leest zijn eindwaarde
 * af op het moment dat hij wordt aangemaakt, en legt dan bij een tweede opbouw de
 * begintoestand vast als eindtoestand. Dat is precies wat hier gebeurde met de videokaart,
 * die daardoor onzichtbaar bleef hangen.
 *
 * Het splitsen van de titel staat in hero-motion.js, gedeeld met de andere hero's.
 *
 * Progressive enhancement: zonder JS -- of met prefers-reduced-motion -- staat alles gewoon
 * op zijn plek. Gaat er onverhoopt iets mis nadat de begintoestand is gezet, dan worden alle
 * inline stijlen weer weggehaald: een binnenkomer die niet beweegt is een schoonheidsfout,
 * een binnenkomer die onzichtbaar blijft is een kapotte pagina.
 *
 * Maten: resources/styles/common/vacancy-hero.scss.
 */

import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import { EASE, minderBeweging, onthulRegels } from './hero-motion';

gsap.registerPlugin(ScrollTrigger);

export default function initVacancyHero() {
  const hero = document.querySelector('[data-vacancy-hero]');

  if (!hero || minderBeweging()) {
    return;
  }

  const media = hero.querySelector('[data-vacancy-hero-media]');
  const notch = hero.querySelector('[data-vacancy-hero-notch]');
  const body = hero.querySelector('[data-vacancy-hero-body]');
  const heading = hero.querySelector('[data-vacancy-hero-heading]');
  const reveals = Array.from(hero.querySelectorAll('[data-vacancy-hero-reveal]'));
  const cards = Array.from(hero.querySelectorAll('[data-vacancy-hero-card]'));

  const beweegbaar = [media, notch, ...reveals, ...cards].filter(Boolean);

  try {
    // De begintoestand meteen, nog voor het lettertype binnen is: anders zie je de hero
    // eerst compleet staan en dan alsnog opbouwen.
    if (media) {
      gsap.set(media, { scale: 1.08 });
    }

    if (notch) {
      gsap.set(notch, { scaleX: 0 });
    }

    gsap.set(reveals, { y: 20, opacity: 0 });
    gsap.set(cards, { x: 36, opacity: 0 });

    parallax(hero, media, body);
    speelIntro(media, notch, reveals, cards);

    // De titel wacht op het lettertype; hero-motion.js regelt dat en het splitsen.
    onthulRegels(heading, { klasse: 'vacancy-hero__line' });
  } catch (fout) {
    // Liever geen beweging dan een onzichtbare binnenkomer.
    gsap.set(beweegbaar, { clearProps: 'all' });

    throw fout;
  }
}

/**
 * De opbouw zelf. Absolute posities op de tijdlijn ("0.35") en geen relatieve: dan
 * verschuift er niets zodra een onderdeel wegvalt bij een vacature zonder video of tag.
 */
function speelIntro(media, notch, reveals, cards) {
  const intro = gsap.timeline();

  // Het beeld komt uit een lichte overmaat terug op zijn plek. Traag en over een lange
  // duur: een snelle zoom leest als een effect, deze leest als een camera die tot rust
  // komt. Die overmaat is meteen de speling die de parallax nodig heeft.
  if (media) {
    intro.to(media, { scale: 1, duration: 2.2, ease: EASE }, 0);
  }

  // scaleX in plaats van width, dan hoeft de browser per frame niets opnieuw uit te
  // rekenen; transform-origin staat links in de SCSS.
  if (notch) {
    intro.to(notch, { scaleX: 1, duration: 1.2, ease: EASE }, 0.55);
  }

  // Tag, feiten en knoppen kort na elkaar, van onderaf. Klein gehouden -- deze regels
  // moeten binnenkomen, niet opvallen.
  if (reveals.length) {
    intro.to(reveals, { y: 0, opacity: 1, duration: 0.9, ease: EASE, stagger: 0.07 }, 0.35);
  }

  // De kaarten rechts komen van opzij: ze horen bij het beeld en niet bij de tekstkolom,
  // en die andere richting maakt dat zichtbaar.
  if (cards.length) {
    intro.to(cards, { x: 0, opacity: 1, duration: 1, ease: EASE, stagger: 0.12 }, 0.6);
  }

  return intro;
}

/**
 * Parallax terwijl de binnenkomer uit beeld scrollt, alleen waar de kaarten naast de tekst
 * staan. In pixels en niet in procenten: de hero is op mobiel veel hoger dan op desktop,
 * dus procenten zouden daar heel anders uitpakken.
 */
function parallax(hero, media, body) {
  if (!media && !body) {
    return;
  }

  gsap.matchMedia().add('(min-width: 1024px)', () => {
    const timeline = gsap.timeline({
      scrollTrigger: {
        trigger: hero,
        start: 'top top',
        end: 'bottom top',
        scrub: true,
      },
    });

    if (media) {
      timeline.to(media, { y: 70, ease: 'none' }, 0);
    }

    if (body) {
      timeline.to(body, { y: -50, ease: 'none' }, 0);
    }
  });
}
